import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SupabaseUser } from '../../../shared/types';
import { setStoredAppCurrencyPrefix } from '../../lib/currency';
import {
  AuthState,
  PendingGoogleUser,
  ResolvedAuthUserPayload,
} from '../../types/index';
import { updateOutlet } from '../outlets/outletThunks';
import { Outlet } from '../../types/outlet';
import {
  fetchCurrentUser,
  login,
  loginWithCode,
  loginWithGoogle,
  logout,
  register,
  setupAccount,
  verifyEmail,
} from './authThunks';

const initialState: AuthState = {
  user: null,
  accessControlCodes: [],
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  error: null,
  pendingGoogleUser: null,
};

const syncStoredCurrencyPrefix = (
  payload: ResolvedAuthUserPayload | null | undefined,
) => {
  setStoredAppCurrencyPrefix(
    payload?.user?.company?.currency_prefix ||
      payload?.user?.outlet?.currency_prefix ||
      null,
  );
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // Auth is *resolved* (signed out) after a reset, not uninitialized —
    // leaving isInitialized false freezes the redirect effect in
    // routes/index.tsx, stranding the user on /sales with a spinner.
    resetAuthState: () => ({ ...initialState, isInitialized: true }),
    setGooglePendingUser: (state, action: PayloadAction<PendingGoogleUser>) => {
      state.pendingGoogleUser = action.payload;
      state.isLoading = false;
    },
    clearGooglePendingUser: (state) => {
      state.pendingGoogleUser = null;
    },
    // Live-patches the signed-in user's outlet from a Supabase Realtime
    // event (see OutletLiveSync) so fields like day_open reflect changes
    // made on other PCs without requiring a re-login. Redux-only — never
    // persisted or synced back to the server from here.
    patchUserOutlet: (state, action: PayloadAction<Partial<Outlet>>) => {
      const outlet = state.user?.outlet;
      if (outlet && outlet.id === action.payload.id) {
        Object.assign(outlet, action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    // Register
    builder.addCase(register.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(register.fulfilled, (state) => {
      state.isLoading = false;
      // Do not authenticate yet, wait for verification
    });
    builder.addCase(register.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Verify Email
    builder.addCase(verifyEmail.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      verifyEmail.fulfilled,
      (state, action: PayloadAction<SupabaseUser | undefined>) => {
        state.isLoading = false;
        if (action.payload) {
          // state.user = action.payload;
          state.isAuthenticated = true;
        }
      },
    );
    builder.addCase(verifyEmail.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Setup Account
    builder.addCase(setupAccount.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(setupAccount.fulfilled, (state) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      // if (_action.payload.user) {
      // state.user = _action.payload.user;
      // }
    });
    builder.addCase(setupAccount.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Login
    builder.addCase(login.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      login.fulfilled,
      (state, action: PayloadAction<ResolvedAuthUserPayload>) => {
        state.isLoading = false;
        if (action.payload.user) {
          syncStoredCurrencyPrefix(action.payload);
          state.user = action.payload.user;
          state.accessControlCodes = action.payload.accessControlCodes;
          state.isAuthenticated = true;
        } else {
          syncStoredCurrencyPrefix(null);
          state.user = null;
          state.accessControlCodes = [];
          state.isAuthenticated = false;
        }
      },
    );
    builder.addCase(login.rejected, (state, action) => {
      state.isLoading = false;
      syncStoredCurrencyPrefix(null);
      state.user = null;
      state.accessControlCodes = [];
      state.isAuthenticated = false;
      state.error = action.payload as string;
    });

    // Login with 4-digit code
    builder.addCase(loginWithCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      loginWithCode.fulfilled,
      (state, action: PayloadAction<ResolvedAuthUserPayload>) => {
        state.isLoading = false;
        if (action.payload.user) {
          syncStoredCurrencyPrefix(action.payload);
          state.user = action.payload.user;
          state.accessControlCodes = action.payload.accessControlCodes;
          state.isAuthenticated = true;
        } else {
          syncStoredCurrencyPrefix(null);
          state.user = null;
          state.accessControlCodes = [];
          state.isAuthenticated = false;
        }
      },
    );
    builder.addCase(loginWithCode.rejected, (state, action) => {
      state.isLoading = false;
      syncStoredCurrencyPrefix(null);
      state.user = null;
      state.accessControlCodes = [];
      state.isAuthenticated = false;
      state.error = action.payload as string;
    });

    // Logout
    builder.addCase(logout.fulfilled, (state) => {
      syncStoredCurrencyPrefix(null);
      state.user = null;
      state.accessControlCodes = [];
      state.isAuthenticated = false;
      state.error = null;
    });

    // Fetch current user
    builder.addCase(fetchCurrentUser.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(
      fetchCurrentUser.fulfilled,
      (state, action: PayloadAction<ResolvedAuthUserPayload>) => {
        state.isLoading = false;
        state.isInitialized = true;
        if (action.payload.user) {
          syncStoredCurrencyPrefix(action.payload);
          state.user = action.payload.user;
          state.accessControlCodes = action.payload.accessControlCodes;
          state.isAuthenticated = true;
        } else {
          syncStoredCurrencyPrefix(null);
          state.user = null;
          state.accessControlCodes = [];
          state.isAuthenticated = false;
        }
      },
    );
    builder.addCase(fetchCurrentUser.rejected, (state) => {
      state.isLoading = false;
      state.isInitialized = true;
      syncStoredCurrencyPrefix(null);
      state.user = null;
      state.accessControlCodes = [];
      state.isAuthenticated = false;
    });

    // Google OAuth login
    builder.addCase(loginWithGoogle.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(
      loginWithGoogle.fulfilled,
      (state, action: PayloadAction<ResolvedAuthUserPayload>) => {
        state.isLoading = false;
        if (action.payload.user) {
          syncStoredCurrencyPrefix(action.payload);
          state.user = action.payload.user;
          state.accessControlCodes = action.payload.accessControlCodes;
          state.isAuthenticated = true;
          state.pendingGoogleUser = null;
        } else if (action.payload.pendingGoogleUser) {
          // New Google user: has a Supabase session but no app profile yet.
          // Routes will redirect to /onboarding when this is set.
          state.pendingGoogleUser = action.payload.pendingGoogleUser;
        }
        // Web flow: user is null and no pendingGoogleUser — onAuthStateChange
        // in App.tsx fires SIGNED_IN and calls fetchCurrentUser separately.
      },
    );
    builder.addCase(loginWithGoogle.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload as string;
    });

    // Sync auth outlet when user's outlet is updated
    builder.addCase(
      updateOutlet.fulfilled,
      (state, action: PayloadAction<Outlet>) => {
        if (state.user?.outlet?.id === action.payload.id) {
          state.user.outlet = action.payload;
        }
      },
    );
  },
});

export const {
  clearError,
  resetAuthState,
  setGooglePendingUser,
  clearGooglePendingUser,
  patchUserOutlet,
} = authSlice.actions;
export default authSlice.reducer;
